import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Loader from "layouts/loader/Loader";
import NontAuthorized401 from "NontAuthorized401";
import Pagination from "components/pagination";
import { Toaster, toast } from "react-hot-toast";
import { apiCall } from "utils/apiClient";
import * as XLSX from "xlsx";
import { LeadAssignOptions, LeadListTable, LeadListToolbar } from "./components/LeadListComponents";
import {
  formatBudget,
  formatDateTime,
  formatLeadStatus,
  getLeadAssignee,
  getLeadAssigneeId,
} from "./leadDetailUtils";

const STATUS_FILTER_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "New", value: "new" },
  { label: "In Progress", value: "in_progress" },
  { label: "Converted", value: "converted" },
  { label: "Closed", value: "closed" },
];

const SPACE_TYPE_OPTIONS = [
  { label: "All Space Types", value: "" },
  { label: "1BHK", value: "1bhk" },
  { label: "2BHK", value: "2bhk" },
  { label: "3BHK", value: "3bhk" },
  { label: "Villa", value: "villa" },
  { label: "Office", value: "office" },
];

const STATUS_BADGES = {
  new: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  converted: "bg-emerald-100 text-emerald-700",
  closed: "bg-gray-100 text-gray-700",
};

const csvEscape = (value) => {
  const text = String(value ?? "");
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const getLeadDisplayRows = (lead) => ({
  customer: lead.customer_name || "-",
  mobile: lead.mobile || "-",
  address: lead.address || "-",
  requirement: lead.requirement_title || "-",
  spaceType: lead.space_type || "-",
  budget: lead.budget || "-",
  status: formatLeadStatus(lead.status),
  channelPartner: lead.channel_partner_name || "-",
  teamAssignment: getLeadAssignee(lead) || "Unassigned",
  createdAt: formatDateTime(lead.created_at),
  notes: String(lead.notes || "-").trim() || "-",
});

function ChannelPartnerLeadsList() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialChannelPartnerId = location.state?.channelPartnerId;
  const initialChannelPartnerName = location.state?.channelPartnerName;

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [spaceType, setSpaceType] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [leads, setLeads] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffLoadError, setStaffLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [fullNotes, setFullNotes] = useState("");
  const [loadError, setLoadError] = useState("");
  const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [assignmentMode, setAssignmentMode] = useState("assign");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [assignmentNote, setAssignmentNote] = useState("");
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(delay);
  }, [searchQuery]);

  const listLeads = async () => {
    setLoading(true);
    setLoadError("");

    const { endpoint, params } = getLeadListRequestConfig(currentPage, recordsPerPage);

    const result = await apiCall({
      endpoint,
      method: "GET",
      params,
    });

    if (result?.status === 200) {
      setLeads(Array.isArray(result.data) ? result.data : []);
      setTotalRecords(result.pagination?.total_records || 0);
      setTotalPages(result.pagination?.total_pages || 0);
      setSelectedLeadIds([]);
      setLoading(false);
      return;
    }

    setLeads([]);
    setTotalRecords(0);
    setTotalPages(0);
    setLoadError(result?.message || "Unable to load channel partner leads.");
    setLoading(false);
  };

  const getLeadListRequestConfig = (page, limit) => {
    const params = {
      page: String(page),
      limit: String(limit),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(status ? { status } : {}),
      ...(spaceType ? { space_type: spaceType } : {}),
      ...(dateRange[0] ? { start_date: dateRange[0].format("YYYY-MM-DD") } : {}),
      ...(dateRange[1] ? { end_date: dateRange[1].format("YYYY-MM-DD") } : {}),
    };

    const endpoint = initialChannelPartnerId
      ? `admin/channel-partner/leads/${initialChannelPartnerId}`
      : "admin/channel-partner/leads";

    return { endpoint, params };
  };

  const fetchAllFilteredLeads = async () => {
    const batchSize = 200;
    let page = 1;
    let totalPagesSeen = 1;
    const allLeads = [];

    while (page <= totalPagesSeen) {
      const { endpoint, params } = getLeadListRequestConfig(page, batchSize);
      const result = await apiCall({
        endpoint,
        method: "GET",
        params,
      });

      const statusCode = Number(result?.status ?? result?.statusCode ?? result?.code ?? 0);
      const isSuccessful = statusCode === 200 || statusCode === 201 || result?.success === true;

      if (!isSuccessful) {
        throw new Error(result?.message || "Unable to fetch all filtered leads.");
      }

      const payload = Array.isArray(result.data) ? result.data : [];
      allLeads.push(...payload);

      totalPagesSeen = Number(result.pagination?.total_pages || totalPagesSeen || 1);

      if (payload.length < batchSize && !result.pagination?.total_pages) {
        break;
      }

      page += 1;
    }

    return allLeads;
  };

  const listStaffMembers = async () => {
    setStaffLoading(true);
    setStaffLoadError("");

    const result = await apiCall({
      endpoint: "admin/channel-partner/admins",
      method: "GET",
    });

    const statusCode = Number(result?.status ?? result?.statusCode ?? result?.code ?? 0);
    const isSuccessful = statusCode === 200 || statusCode === 201 || result?.success === true;

    if (!isSuccessful) {
      setStaffMembers([]);
      setStaffLoadError(result?.message || "Unable to load team members.");
      setStaffLoading(false);
      return;
    }

    const payload = result?.data?.data ?? result?.data ?? [];
    setStaffMembers(Array.isArray(payload) ? payload : []);
    setStaffLoading(false);
  };

  useEffect(() => {
    listLeads();
  }, [currentPage, recordsPerPage, debouncedSearch, status, spaceType, dateRange]);

  useEffect(() => {
    listStaffMembers();
  }, []);

  const clearFilters = () => {
    setCurrentPage(1);
    setSearchQuery("");
    setDebouncedSearch("");
    setStatus("");
    setSpaceType("");
    setDateRange([]);
  };

  const heading = initialChannelPartnerId
    ? `${initialChannelPartnerName || "Channel Partner"} Leads`
    : "All Channel Partner Leads";

  const selectedLeadCount = selectedLeadIds.length;
  const selectedLeadCountLabel = selectedLeadCount
    ? `${selectedLeadCount} lead${selectedLeadCount === 1 ? "" : "s"} selected`
    : "No leads selected";
  const isBulkAssignment = selectedLeadCount > 0 && !selectedLead;
  const allCurrentPageSelected = leads.length > 0 && leads.every((lead) => selectedLeadIds.includes(String(lead.id)));

  const toggleLeadSelection = (leadId) => {
    const nextId = String(leadId);
    setSelectedLeadIds((current) =>
      current.includes(nextId) ? current.filter((id) => id !== nextId) : [...current, nextId],
    );
  };

  const toggleSelectAllCurrentPage = () => {
    const currentPageIds = leads
      .map((lead) => String(lead.id))
      .filter((id) => Boolean(id));

    if (currentPageIds.length === 0) {
      return;
    }

    setSelectedLeadIds((current) => {
      const currentSet = new Set(current);
      const allSelected = currentPageIds.every((id) => currentSet.has(id));

      if (allSelected) {
        return current.filter((id) => !currentPageIds.includes(id));
      }

      const nextSet = new Set(current);
      currentPageIds.forEach((id) => nextSet.add(id));
      return Array.from(nextSet);
    });
  };

  const exportLeadsToCsv = () => {
    const exportRows = selectedLeadIds.length > 0
      ? leads.filter((lead) => selectedLeadIds.includes(String(lead.id)))
      : leads;

    if (exportRows.length === 0) {
      toast.error("No leads available to export.");
      return;
    }

    const headers = [
      "Sr No",
      "Customer",
      "Mobile",
      "Address",
      "Requirement",
      "Space Type",
      "Budget",
      "Status",
      "Channel Partner",
      "Team Assignment",
      "Created At",
      "Notes",
    ];

    const lines = [
      headers.map(csvEscape).join(","),
      ...exportRows.map((lead, index) =>
        [
          index + 1,
          lead.customer_name || "-",
          lead.mobile || "-",
          lead.address || "-",
          lead.requirement_title || "-",
          lead.space_type || "-",
          lead.budget || "-",
          lead.status ? lead.status.replace(/_/g, " ") : "-",
          lead.channel_partner_name || "-",
          getLeadAssignee(lead) || "Unassigned",
          formatDateTime(lead.created_at),
          String(lead.notes || "-").trim() || "-",
        ].map(csvEscape).join(","),
      ),
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `channel-partner-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${exportRows.length} lead${exportRows.length === 1 ? "" : "s"} to CSV.`);
  };

  const exportLeadsToExcel = async () => {
    setExportingExcel(true);

    try {
      const exportRows = await fetchAllFilteredLeads();

      if (exportRows.length === 0) {
        toast.error("No leads available to export.");
        return;
      }

      const sheetRows = exportRows.map((lead, index) => ({
        "Sr No": index + 1,
        Customer: getLeadDisplayRows(lead).customer,
        Mobile: getLeadDisplayRows(lead).mobile,
        Address: getLeadDisplayRows(lead).address,
        Requirement: getLeadDisplayRows(lead).requirement,
        "Space Type": getLeadDisplayRows(lead).spaceType,
        Budget: getLeadDisplayRows(lead).budget,
        Status: getLeadDisplayRows(lead).status,
        "Channel Partner": getLeadDisplayRows(lead).channelPartner,
        "Team Assignment": getLeadDisplayRows(lead).teamAssignment,
        "Created At": getLeadDisplayRows(lead).createdAt,
        Notes: getLeadDisplayRows(lead).notes,
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `channel-partner-leads-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Exported ${exportRows.length} lead${exportRows.length === 1 ? "" : "s"} to Excel.`);
    } catch (error) {
      toast.error(error?.message || "Unable to export Excel file.");
    } finally {
      setExportingExcel(false);
    }
  };

  const openAssignmentModal = (lead) => {
    const currentAssigneeId = getLeadAssigneeId(lead);
    const currentAssigneeName = getLeadAssignee(lead);

    setSelectedLeadIds([]);
    setSelectedLead(lead);
    setAssignmentMode(currentAssigneeId ? "reassign" : "assign");
    setSelectedStaffId(currentAssigneeId ? String(currentAssigneeId) : "");
    setAssignmentNote(currentAssigneeName ? `Current assignee: ${currentAssigneeName}` : "");
    setAssignmentModalVisible(true);
  };

  const openBulkAssignmentModal = () => {
    if (selectedLeadIds.length === 0) {
      toast.error("Please select at least one lead.");
      return;
    }

    setSelectedLead(null);
    setAssignmentMode("assign");
    setSelectedStaffId("");
    setAssignmentNote("");
    setAssignmentModalVisible(true);
  };

  const closeAssignmentModal = () => {
    setAssignmentModalVisible(false);
    setSelectedLead(null);
    setAssignmentMode("assign");
    setSelectedStaffId("");
    setAssignmentNote("");
    setSavingAssignment(false);
  };

  const submitAssignment = async () => {
    if (!selectedStaffId) {
      toast.error("Please select a team member.");
      return;
    }

    const bulkTargetLeadIds = selectedLeadIds.length > 0 ? [...selectedLeadIds] : [];
    const bulkTargetLeads = bulkTargetLeadIds.length
      ? leads.filter((lead) => bulkTargetLeadIds.includes(String(lead.id)))
      : [];
    const isBulkAssignment = bulkTargetLeads.length > 0 && !selectedLead;

    if (!isBulkAssignment && !selectedLead?.id) {
      toast.error("Lead information is missing.");
      return;
    }

    setSavingAssignment(true);

    const assignSingleLead = async (lead) => {
      const payload = {
        assigned_admin_id: Number(selectedStaffId),
        status: "in_progress",
        ...(assignmentNote.trim() ? { note: assignmentNote.trim() } : {}),
      };

      return apiCall({
        endpoint: `admin/channel-partner/lead/${lead.id}/assign`,
        method: "PUT",
        data: payload,
      });
    };

    if (isBulkAssignment) {
      const results = await Promise.allSettled(bulkTargetLeads.map((lead) => assignSingleLead(lead)));
      const successCount = results.filter((item) => {
        if (item.status !== "fulfilled") return false;
        const result = item.value;
        const statusCode = Number(result?.status ?? result?.statusCode ?? result?.code ?? 0);
        return statusCode === 200 || statusCode === 201 || result?.success === true;
      }).length;
      const failureCount = results.length - successCount;

      if (successCount === 0) {
        setSavingAssignment(false);
        toast.error("Unable to assign the selected leads.");
        return;
      }

      await listLeads();
      closeAssignmentModal();
      setSelectedLeadIds([]);

      if (failureCount > 0) {
        toast.error(`${successCount} lead${successCount === 1 ? "" : "s"} assigned, ${failureCount} failed.`);
        return;
      }

      toast.success(`${successCount} lead${successCount === 1 ? "" : "s"} assigned successfully.`);
      return;
    }

    const result = await assignSingleLead(selectedLead);
    const statusCode = Number(result?.status ?? result?.statusCode ?? result?.code ?? 0);
    const isSuccessful = statusCode === 200 || statusCode === 201 || result?.success === true;

    if (!isSuccessful) {
      setSavingAssignment(false);
      toast.error(result?.message || `Unable to ${assignmentMode} lead.`);
      return;
    }

    const successVerb = assignmentMode === "reassign" ? "reassigned" : "assigned";
    toast.success(result?.message || `Lead ${successVerb} successfully.`);
    closeAssignmentModal();
    await listLeads();
  };

  return (
    <>
      {localStorage.getItem("Token") ? (
        <DashboardLayout>
          <DashboardNavbar />
          <Toaster position="top-center" reverseOrder={false} />

          {loading ? (
            <>
              <div className="relative bg-white h-screen overflow-hidden" />
              <div className="flex justify-center">
                <div className="absolute top-[30%]">
                  <Loader />
                </div>
              </div>
            </>
          ) : (
            <div className="mt-6 px-4 sm:px-6">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <LeadListToolbar
                  heading={heading}
                  countLabel={`Showing ${leads.length} of ${totalRecords}`}
                  selectedLeadCountLabel={selectedLeadCountLabel}
                  loadingExcel={exportingExcel}
                  onExportCsv={exportLeadsToCsv}
                  onExportExcel={exportLeadsToExcel}
                  onBulkAssign={openBulkAssignmentModal}
                  hasLeads={leads.length > 0}
                  selectedLeadCount={selectedLeadCount}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  dateRange={dateRange}
                  setDateRange={(dates) => {
                    setDateRange(dates);
                    setCurrentPage(1);
                  }}
                  status={status}
                  setStatus={(value) => {
                    setStatus(value);
                    setCurrentPage(1);
                  }}
                  spaceType={spaceType}
                  setSpaceType={(value) => {
                    setSpaceType(value);
                    setCurrentPage(1);
                  }}
                  statusOptions={STATUS_FILTER_OPTIONS}
                  spaceTypeOptions={SPACE_TYPE_OPTIONS}
                  onClearFilters={clearFilters}
                />

                {loadError && (
                  <div className="mx-4 mt-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                    {loadError}
                  </div>
                )}

                <LeadListTable
                  leads={leads}
                  currentPage={currentPage}
                  recordsPerPage={recordsPerPage}
                  selectedLeadIds={selectedLeadIds}
                  allCurrentPageSelected={allCurrentPageSelected}
                  onToggleSelectAll={toggleSelectAllCurrentPage}
                  onToggleLeadSelection={toggleLeadSelection}
                  statusBadges={STATUS_BADGES}
                  onViewLead={(lead) =>
                    navigate("/channel-partner-lead-detail", {
                      state: {
                        lead,
                        channelPartnerId: initialChannelPartnerId,
                        channelPartnerName: initialChannelPartnerName,
                      },
                    })
                  }
                  onAssignLead={openAssignmentModal}
                  getLeadAssignee={getLeadAssignee}
                  getLeadAssigneeId={getLeadAssigneeId}
                  formatBudget={formatBudget}
                  formatDateTime={formatDateTime}
                />

                <div className="px-2 py-2 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => setCurrentPage(page)}
                    recordsPerPage={recordsPerPage}
                    onRecordsPerPageChange={(value) => {
                      setCurrentPage(1);
                      setRecordsPerPage(value);
                    }}
                    totalRecords={totalRecords}
                    count={leads.length}
                    showFirstLastControls
                    compact
                  />
                </div>
              </div>

              <Modal
                title="Lead Notes"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                maskStyle={{ backgroundColor: "transparent" }}
              >
                <p>{fullNotes || "-"}</p>
              </Modal>

              <Modal
                title={
                  isBulkAssignment
                    ? "Assign Selected Leads"
                    : `${assignmentMode === "reassign" ? "Reassign" : "Assign"} Lead`
                }
                open={assignmentModalVisible}
                onCancel={closeAssignmentModal}
                footer={null}
                maskStyle={{ backgroundColor: "transparent" }}
                destroyOnClose
              >
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                    {isBulkAssignment ? (
                      <div className="font-medium text-gray-900">
                        {selectedLeadCount} selected lead{selectedLeadCount === 1 ? "" : "s"}
                      </div>
                    ) : (
                      <div className="font-medium text-gray-900">
                        {selectedLead?.customer_name || "Lead"}{" "}
                        {selectedLead?.mobile ? `(${selectedLead.mobile})` : ""}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-gray-500">
                      {isBulkAssignment
                        ? "Choose one team member and the system will assign every selected lead."
                        : assignmentMode === "reassign"
                          ? "This lead already has a team member. Select another person to move it."
                          : "Choose a team member to start the calling workflow."}
                    </div>
                  </div>

                  <LeadAssignOptions
                    admins={staffMembers}
                    adminLoading={staffLoading}
                    adminLoadError={staffLoadError}
                    selectedAdminId={selectedStaffId}
                    onChangeAdmin={setSelectedStaffId}
                  />

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Internal Note</label>
                    <textarea
                      value={assignmentNote}
                      onChange={(event) => setAssignmentNote(event.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      placeholder="Optional note for the team assignment"
                    />
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeAssignmentModal}
                      className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      disabled={savingAssignment}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={submitAssignment}
                      className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
                      disabled={savingAssignment}
                    >
                      {savingAssignment
                        ? "Saving..."
                        : `${assignmentMode === "reassign" ? "Reassign" : "Assign"} Lead`}
                    </button>
                  </div>
                </div>
              </Modal>
            </div>
          )}
        </DashboardLayout>
      ) : (
        <NontAuthorized401 />
      )}
    </>
  );
}

export default ChannelPartnerLeadsList;
